interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime?: string;
  size?: string;
}

interface DriveSearchResult {
  files: DriveFile[];
  total: number;
}

interface DriveFileContent {
  mimeType: string;
  content: string;
}

class DriveService {
  // Use the DRIVE_MCP_URL from environment or fallback to localhost
  private baseUrl = import.meta.env.VITE_DRIVE_MCP_URL || 
                    (import.meta.env.MODE === 'production' 
                      ? 'https://drive-mcp468.fly.dev' 
                      : 'http://localhost:3004');
  private accessToken: string | null = null;

  constructor() {
    console.log('DriveService initialized with URL:', this.baseUrl);
  }

  // Set access token for Drive API
  async setAccessToken(token: string): Promise<boolean> {
    this.accessToken = token;
    try {
      const response = await this.makeRequest('/set-token', {
        method: 'POST',
        body: JSON.stringify({ accessToken: token }),
      });
      console.log('Drive access token set successfully');
      return response.success;
    } catch (error) {
      console.error('Failed to set Drive access token:', error);
      return false;
    }
  }

  // Check if Drive service is healthy and authenticated
  async checkHealth(): Promise<{ status: string; drive: string; oauth: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return await response.json();
    } catch (error) {
      console.error('Drive health check failed:', error);
      return { status: 'error', drive: 'not initialized', oauth: 'not initialized' };
    }
  }

  // Search for files in Google Drive
  async searchFiles(query: string): Promise<DriveSearchResult> {
    if (!this.accessToken) {
      throw new Error('Drive not authenticated. Please log in first.');
    }

    try {
      const response = await this.makeRequest('/call-tool', {
        method: 'POST',
        body: JSON.stringify({
          name: 'gdrive_search',
          arguments: { query }
        }),
      });

      // Parse the response text to extract file information
      const content = response.content?.[0]?.text || '';
      const lines = content.split('\n').filter((line: string) => line.trim());
      
      if (lines.length === 0 || lines[0].includes('Found 0 files')) {
        return { files: [], total: 0 };
      }

      // Extract total count from first line
      const totalMatch = lines[0].match(/Found (\d+) files/);
      const total = totalMatch ? parseInt(totalMatch[1]) : 0;

      // Parse file entries (skip the first line which contains the count)
      const files: DriveFile[] = lines.slice(1).map((line: string) => {
        // Format: "filename (mimeType) - ID: fileId"
        const match = line.match(/^(.+?) \((.+?)\) - ID: (.+)$/);
        if (match) {
          return {
            id: match[3],
            name: match[1],
            mimeType: match[2]
          };
        }
        return null;
      }).filter((line: DriveFile | null): line is DriveFile => line !== null);

      return { files, total };
    } catch (error) {
      console.error('Drive search failed:', error);
      throw new Error('Failed to search Google Drive files');
    }
  }

  // Read file content from Google Drive
  async readFile(fileId: string): Promise<DriveFileContent> {
    if (!this.accessToken) {
      throw new Error('Drive not authenticated. Please log in first.');
    }

    try {
      const response = await this.makeRequest('/call-tool', {
        method: 'POST',
        body: JSON.stringify({
          name: 'gdrive_read_file',
          arguments: { file_id: fileId }
        }),
      });

      if (response.isError) {
        throw new Error(response.content?.[0]?.text || 'Failed to read file');
      }

      const content = response.content?.[0]?.text || '';
      
      // For now, we'll assume text content. In a real implementation,
      // you might want to detect the MIME type from the file metadata
      return {
        mimeType: 'text/plain',
        content
      };
    } catch (error) {
      console.error('Drive file read failed:', error);
      throw new Error('Failed to read Google Drive file');
    }
  }

  // List recent files (using search with no query to get all files)
  async listRecentFiles(): Promise<DriveSearchResult> {
    return this.searchFiles(''); // Empty query returns recent files
  }

  private async makeRequest(endpoint: string, options: RequestInit): Promise<any> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }
}

export const driveService = new DriveService();
export type { DriveFile, DriveSearchResult, DriveFileContent };